export type RuntimeConfiguration = {
  apiBaseUrl: string;
  environment: 'Production' | 'Staging' | 'Development';
  releaseId: string;
};

let current: RuntimeConfiguration | null = null;

export function validateRuntimeConfiguration(value: unknown, pageUrl: string): RuntimeConfiguration {
  if (!value || typeof value !== 'object') throw new Error('Runtime configuration is missing.');
  const input = value as Partial<RuntimeConfiguration>;
  if (
    !input.apiBaseUrl ||
    !input.releaseId ||
    !['Production', 'Staging', 'Development'].includes(input.environment ?? '')
  )
    throw new Error('Runtime configuration is incomplete.');
  const url = new URL(input.apiBaseUrl);
  const page = new URL(pageUrl);
  const loopback = ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
  if (
    (url.protocol !== 'https:' && !(input.environment === 'Development' && loopback && url.protocol === 'http:')) ||
    url.username ||
    url.password ||
    url.search ||
    url.hash ||
    !/^\/v1\/?$/.test(url.pathname)
  )
    throw new Error('The configured API address is invalid.');
  if (
    input.environment === 'Staging' &&
    (!page.hostname.includes('-staging.') || !url.hostname.includes('-staging.')) &&
    !(['localhost', '127.0.0.1'].includes(page.hostname) && loopback)
  )
    throw new Error('Staging requires staging API and administrator hostnames.');
  if (input.environment === 'Production' && (page.hostname.includes('-staging.') || url.hostname.includes('-staging.')))
    throw new Error('Production cannot use staging endpoints.');
  return { apiBaseUrl: url.href.replace(/\/$/, ''), environment: input.environment!, releaseId: input.releaseId };
}

export async function loadRuntimeConfiguration(): Promise<RuntimeConfiguration> {
  if (import.meta.env.DEV) {
    current = validateRuntimeConfiguration(
      {
        apiBaseUrl: import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5080/v1',
        environment: 'Development',
        releaseId: 'development',
      },
      window.location.href,
    );
  } else {
    const response = await fetch('/_configuration/runtime.json', { cache: 'no-store', credentials: 'same-origin' });
    if (!response.ok) throw new Error('Runtime configuration could not be loaded.');
    current = validateRuntimeConfiguration(await response.json(), window.location.href);
  }
  return current;
}

export function getRuntimeConfiguration(): RuntimeConfiguration {
  if (!current) throw new Error('Runtime configuration must be loaded before the application.');
  return current;
}
